import React, { useRef } from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  Skeleton,
  Grid,
} from "@mui/material";
import { motion } from "framer-motion";
import { useGetPlansQuery } from "../../../Store/api/queries";
// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Navigation,
  Pagination,
  Autoplay,
  EffectCoverflow,
} from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";
import { SportsGymnastics } from "@mui/icons-material";
import SectionTitle from "./SectionTitle";
import PlanCard from "./PlanCard";

// Main Component
export default function Cooperation() {
  const theme = useTheme();
  const { data, isLoading } = useGetPlansQuery({ limit: 100 });

  const getPlansByType = (type) => {
    if (!data?.data) return [];
    return data.data.filter(
      (plan) => plan.type === type && plan.isActive !== false,
    );
  };

  const gymPlans = getPlansByType("gym");
  const coachPlans = getPlansByType("coach");

  // Swiper settings with Coverflow effect
  const swiperSettings = (isDesktop) => ({
    modules: [Navigation, Pagination, Autoplay],
    grabCursor: true,
    centeredSlides: false,
    spaceBetween: 24,
    pagination: {
      clickable: true,
      dynamicBullets: true,
      renderBullet: function (index, className) {
        return `<span class="${className}" style="background: ${theme.palette.primary.main}; opacity: 0.3;"></span>`;
      },
    },
    autoplay: {
      delay: 3500,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    },
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev",
    },
    breakpoints: {
      0: {
        slidesPerView: 1,
        spaceBetween: 16,
      },
      640: {
        slidesPerView: 1.5,
        spaceBetween: 20,
      },
      900: {
        slidesPerView: 2,
        spaceBetween: 24,
      },
      1200: {
        slidesPerView: 2.5,
        spaceBetween: 30,
      },
      1400: {
        slidesPerView: 3,
        spaceBetween: 30,
      },
    },
  });

  // Loading State
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100svh",
          py: 8,
          px: { xs: 2, md: 4 },
          // background:
          //   "linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)",
        }}
      >
        <Container maxWidth="xl">
          <Box textAlign="center" mb={6}>
            <Skeleton
              variant="text"
              width={300}
              height={60}
              sx={{ mx: "auto", bgcolor: "rgba(255,255,255,0.1)" }}
            />
            <Skeleton
              variant="text"
              width={400}
              height={30}
              sx={{ mx: "auto", bgcolor: "rgba(255,255,255,0.05)" }}
            />
          </Box>
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Skeleton
                  variant="rectangular"
                  height={520}
                  sx={{ borderRadius: 6, bgcolor: "rgba(255,255,255,0.05)" }}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  // Empty State
  if (!gymPlans.length && !coachPlans.length) {
    return (
      <Box
        sx={{
          minHeight: "60svh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          p: 4,
          // background:
          //   "linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)",
        }}
      >
        <Box
          sx={{
            p: 6,
            borderRadius: 6,
            background: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            maxWidth: 500,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: "#fff", mb: 2 }}
          >
            به زودی...
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.6)" }}>
            در حال آماده‌سازی طرح‌های ویژه هستیم. با ما همراه باشید.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100svh",
        py: { xs: 6, md: 10 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xxl" sx={{ position: "relative", zIndex: 2 }}>
        {/* Main Header */}
        <Box textAlign="center" mb={10}>
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box
              sx={{
                display: "inline-block",
                mb: 3,
                px: 3,
                py: 1,
                borderRadius: 10,
                background: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 500 }}
              >
                💎 انتخاب هوشمندانه برای رشد کسب‌وکار
              </Typography>
            </Box>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 900,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                mb: 2,
                fontSize: { xs: "1.5rem", md: "2.5rem" },
                textShadow: "0 0 40px rgba(79, 195, 247, 0.3)",
              }}
            >
              طرح‌های همکاری
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.6)",
                maxWidth: 600,
                mx: "auto",
                fontWeight: 400,
                lineHeight: 1.6,
                fontSize: "1rem",
              }}
            >
              با حرفه‌ای‌ترین ابزارها، مسیر موفقیت را هموار کنید
            </Typography>
          </motion.div>
        </Box>

        {/* Coach Plans Section */}
        {coachPlans.length > 0 && (
          <Box>
            <SectionTitle
              icon={<SportsGymnastics />}
              title="طرح‌های مربیان"
              subtitle="ابزارهای تخصصی برای مربیگری"
              count={coachPlans.length}
              color={theme.palette.warning.main}
            />

            <Swiper {...swiperSettings(true)} style={{ padding: "1rem" }}>
              {coachPlans.map((plan, index) => (
                <SwiperSlide key={plan._id}>
                  <PlanCard plan={plan} index={index} />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        )}

        {/* Gym Plans Section */}
        {gymPlans.length > 0 && (
          <Box mb={12}>
            <SectionTitle
              icon={<SportsGymnastics />}
              title="طرح‌های باشگاه‌ها"
              subtitle="مدیریت حرفه‌ای و رشد پایدار"
              count={gymPlans.length}
              color={theme.palette.secondary.main}
            />

            <Swiper {...swiperSettings(true)}>
              {gymPlans.map((plan, index) => (
                <SwiperSlide style={{ height: "100%" }} key={plan._id}>
                  <PlanCard plan={plan} index={index} />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        )}
      </Container>
    </Box>
  );
}
